import React, { useState } from 'react';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronDown, Filter, Calendar, Users, Fish, X, Mountain, Loader2, Sliders } from 'lucide-react';
import { ActivityFilters } from '@/components/homepage/ActivityTypeFilter';
import { AnimalTypeFilter } from '@/components/homepage/AnimalTypeFilter';
import { AdventureTypeFilter } from '@/components/homepage/AdventureTypeFilter';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { SEASONS, getCurrentSeason } from '@/constants/seasons';
import { useDebouncedFilterUpdate } from '@/hooks/useDebounce';
import { log } from '@/utils/logger';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from 'react-i18next';

interface FilterPopoverProps {
  activityFilters: ActivityFilters;
  selectedAnimalTypes: string[];
  selectedAdventureTypes: string[];
  selectedSeason: string | null;
  groupSize: [number, number];
  onActivityFilterChange: (filters: ActivityFilters) => void;
  onAnimalTypeChange: (selected: string[]) => void;
  onAdventureTypeChange: (selected: string[]) => void;
  onSeasonChange: (season: string | null) => void;
  onGroupSizeChange: (size: [number, number]) => void;
  onClearFilters: () => void;
  showClearFilters: boolean;
  /** Hide Season and Group size (they do not affect results yet). Default true. */
  hidePlaceboFilters?: boolean;
  /** 'compact' = single Filters button + popover/dialog; 'inline' = full row of filter chips. Default 'inline'. */
  variant?: 'compact' | 'inline';
}

interface MobileFilterContentProps {
  isFilterUpdatePending: boolean;
  activityFilters: ActivityFilters;
  onActivityFilterChange: (filters: ActivityFilters) => void;
  selectedAnimalTypes: string[];
  onAnimalTypeChange: (types: string[]) => void;
  selectedAdventureTypes: string[];
  onAdventureTypeChange: (types: string[]) => void;
  hidePlaceboFilters: boolean;
  selectedSeason: string | null;
  onSeasonChange: (season: string | null) => void;
  groupSize: [number, number];
  onGroupSizeChange: (size: [number, number]) => void;
  showClearFilters: boolean;
  onClearFilters: () => void;
  onClose: () => void;
}

function MobileFilterContent({
  isFilterUpdatePending,
  activityFilters,
  onActivityFilterChange,
  selectedAnimalTypes,
  onAnimalTypeChange,
  selectedAdventureTypes,
  onAdventureTypeChange,
  hidePlaceboFilters,
  selectedSeason,
  onSeasonChange,
  groupSize,
  onGroupSizeChange,
  showClearFilters,
  onClearFilters,
  onClose,
}: MobileFilterContentProps) {
  const { t } = useTranslation('common');
  const showAnimalFilter = activityFilters.showAnimalActivities;
  const showAdventureFilter = activityFilters.showAdventureActivities;

  return (
    <div className="space-y-6 p-1">
      {/* Filter Status Indicator */}
      <AnimatePresence>
        {isFilterUpdatePending && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-sm font-medium"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t('filters.updatingFilters')}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Experience Type Filter */}
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-900 text-lg">{t('filters.experienceTypes')}</h3>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
            <input
              type="checkbox"
              checked={activityFilters.showAnimalActivities}
              onChange={(e) => onActivityFilterChange({
                ...activityFilters,
                showAnimalActivities: e.target.checked
              })}
              className="w-5 h-5 text-emerald-600 bg-white border-2 border-gray-300 rounded focus:ring-emerald-500"
            />
            <Fish className="h-5 w-5 text-emerald-600" />
            <span className="text-gray-700 font-medium">{t('filters.animalExperiences')}</span>
          </label>

          <label className="flex items-center space-x-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
            <input
              type="checkbox"
              checked={activityFilters.showAdventureActivities}
              onChange={(e) => onActivityFilterChange({
                ...activityFilters,
                showAdventureActivities: e.target.checked
              })}
              className="w-5 h-5 text-emerald-600 bg-white border-2 border-gray-300 rounded focus:ring-emerald-500"
            />
            <Mountain className="h-5 w-5 text-emerald-600" />
            <span className="text-gray-700 font-medium">{t('filters.adventureExperiences')}</span>
          </label>
        </div>
      </div>

      {/* Animal Type Filter - Conditional */}
      {showAnimalFilter && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 text-lg">{t('filters.animalTypes')}</h3>
            {selectedAnimalTypes.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAnimalTypeChange([])}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                {t('filters.clearAll')}
              </Button>
            )}
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <AnimalTypeFilter selected={selectedAnimalTypes} onChange={onAnimalTypeChange} />
          </div>
        </div>
      )}

      {/* Adventure Type Filter - Conditional */}
      {showAdventureFilter && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 text-lg">{t('filters.adventureTypes')}</h3>
            {selectedAdventureTypes.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAdventureTypeChange([])}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                {t('filters.clearAll')}
              </Button>
            )}
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <AdventureTypeFilter selected={selectedAdventureTypes} onChange={onAdventureTypeChange} />
          </div>
        </div>
      )}

      {!hidePlaceboFilters && (
        <>
          {/* Season Filter */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-lg">{t('filters.bestSeasonToVisit')}</h3>
              {selectedSeason && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSeasonChange(null)}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  {t('filters.clear')}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {SEASONS.map((season) => {
                const isSelected = selectedSeason === season.id;
                const isCurrent = getCurrentSeason().id === season.id;
                return (
                  <button
                    key={season.id}
                    onClick={() => onSeasonChange(isSelected ? null : season.id)}
                    className={cn(
                      "p-4 rounded-xl border-2 transition-all duration-200 text-left",
                      isSelected
                        ? "bg-emerald-50 border-emerald-500 text-emerald-700 shadow-md"
                        : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{season.emoji}</span>
                      <span className="font-medium text-sm">{season.name}</span>
                      {isCurrent && !isSelected && (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full font-medium">
                          {t('filters.current')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600">{season.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Group Size Filter */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-lg">{t('filters.groupSize')}</h3>
              {(groupSize[0] !== 1 || groupSize[1] !== 20) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onGroupSizeChange([1, 20])}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  {t('filters.reset')}
                </Button>
              )}
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-700 mb-3">{t('filters.quickSelect')}</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                    <Button
                      key={num}
                      variant="outline"
                      size="sm"
                      onClick={() => onGroupSizeChange([num, num])}
                      className={cn(
                        "w-full transition-colors border-2 font-medium",
                        groupSize[0] === num && groupSize[1] === num
                          ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                          : "bg-white hover:bg-gray-50 border-gray-200 hover:border-emerald-300"
                      )}
                    >
                      {num}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onGroupSizeChange([9, 20])}
                  className={cn(
                    "w-full mt-2 transition-colors border-2 font-medium",
                    groupSize[0] >= 9 && groupSize[1] >= 9
                      ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                      : "bg-white hover:bg-gray-50 border-gray-200 hover:border-emerald-300"
                  )}
                >
                  {t('filters.ninePlusPeople')}
                </Button>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-700 mb-3">{t('filters.orSelectRange')}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block font-medium">{t('filters.minimum')}</label>
                    <select
                      value={groupSize[0]}
                      onChange={(e) => {
                        const min = parseInt(e.target.value);
                        onGroupSizeChange([min, Math.max(min, groupSize[1])]);
                      }}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                    >
                      {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block font-medium">{t('filters.maximum')}</label>
                    <select
                      value={groupSize[1]}
                      onChange={(e) => {
                        const max = parseInt(e.target.value);
                        onGroupSizeChange([Math.min(groupSize[0], max), max]);
                      }}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                    >
                      {Array.from({ length: 20 }, (_, i) => i + 1).filter(num => num >= groupSize[0]).map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Clear All Filters Button */}
      {showClearFilters && (
        <div className="pt-2">
          <Button
            variant="outline"
            onClick={() => { onClearFilters(); onClose(); }}
            className="w-full h-12 text-emerald-600 border-2 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 font-medium rounded-xl"
          >
            <X className="h-5 w-5 mr-2" />
            {t('filters.clearAllFilters')}
          </Button>
        </div>
      )}
    </div>
  );
}

export function FilterPopover({
  activityFilters,
  selectedAnimalTypes,
  selectedAdventureTypes,
  selectedSeason,
  groupSize,
  onActivityFilterChange,
  onAnimalTypeChange,
  onAdventureTypeChange,
  onSeasonChange,
  onGroupSizeChange,
  onClearFilters,
  showClearFilters,
  hidePlaceboFilters = true,
  variant = 'inline'
}: FilterPopoverProps) {
  const { t } = useTranslation('common');
  const { isMobile } = useResponsive();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [compactOpen, setCompactOpen] = useState(false);

  const {
    scheduleFilterUpdate,
    isUpdating: isFilterUpdatePending,
    cancel: cancelFilterUpdate
  } = useDebouncedFilterUpdate(500);

  const logger = log.component('FilterPopover');

  const handleActivityFilterChange = (filters: ActivityFilters) => {
    logger.debug('Activity filter change requested', { filters });
    
    // Immediate UI update for visual feedback
    onActivityFilterChange(filters);
    
    // Schedule debounced update for performance optimization
    scheduleFilterUpdate(() => {
      // This function will be called after the debounce delay
      // The filter change has already been applied above
    }, 'activityFilters');
  };

  const handleAnimalTypeChange = (types: string[]) => {
    logger.debug('Animal type filter change requested', { types });
    
    // Immediate UI update for visual feedback
    onAnimalTypeChange(types);
    
    // Note: Animal types would need their own debounced action in the store
    // For now, we'll use the immediate version
  };

  const handleAdventureTypeChange = (types: string[]) => {
    logger.debug('Adventure type filter change requested', { types });
    
    // Immediate UI update for visual feedback
    onAdventureTypeChange(types);
    
    // Note: Adventure types would need their own debounced action in the store
    // For now, we'll use the immediate version
  };

  const handleSeasonChange = (season: string | null) => {
    logger.debug('Season filter change requested', { season });
    
    // Immediate UI update for visual feedback
    onSeasonChange(season);
    
    // Note: Season would need its own debounced action in the store
    // For now, we'll use the immediate version
  };

  const handleGroupSizeChange = (size: [number, number]) => {
    logger.debug('Group size filter change requested', { size });
    
    // Immediate UI update for visual feedback
    onGroupSizeChange(size);
    
    // Note: Group size would need its own debounced action in the store
    // For now, we'll use the immediate version
  };

  // Determine which type-specific filters to show
  const showAnimalFilter = activityFilters.showAnimalActivities;
  const showAdventureFilter = activityFilters.showAdventureActivities;

  // Count only filters that actually affect results (exclude Season and Group size until backend supports them)
  const activeFilterCount = [
    selectedAnimalTypes.length > 0,
    selectedAdventureTypes.length > 0,
    (!activityFilters.showAnimalActivities || !activityFilters.showAdventureActivities)
  ].filter(Boolean).length;

  // Compact variant: single Filters button + popover (desktop) or dialog (mobile) – for sticky strip
  // Button matches search bar height and style (min-h, rounded-xl, border, shadow) for visual harmony
  if (variant === 'compact') {
    const compactTrigger = (
      <Button
        variant="outline"
        className={cn(
          "min-h-[48px] h-12 rounded-xl border border-gray-200 gap-2 bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md",
          "flex items-center justify-center px-4",
          isFilterUpdatePending && "opacity-75",
          activeFilterCount > 0 && "border-emerald-500 bg-emerald-50 text-emerald-700"
        )}
      >
        <Filter className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
        <span className="sr-only sm:not-sr-only font-medium text-sm sm:text-base">{t('filters.title')}</span>
        {activeFilterCount > 0 && (
          <span className="ml-0.5 bg-primary text-white text-xs rounded-full min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center">
            {activeFilterCount}
          </span>
        )}
      </Button>
    );
    if (isMobile) {
      return (
        <>
          <AnimatePresence>
            {isFilterUpdatePending && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-1.5 text-amber-700 text-xs"
              >
                <Loader2 className="h-3 w-3 animate-spin" />
                {t('filters.updating')}
              </motion.span>
            )}
          </AnimatePresence>
          <Dialog open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
            <DialogTrigger asChild>{compactTrigger}</DialogTrigger>
            <DialogContent className="sm:max-w-md w-[95vw] max-h-[90vh] overflow-hidden p-0 bg-white border-gray-200">
              <DialogHeader className="px-6 py-4 border-b border-gray-100 bg-white">
                <DialogTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Filter className="h-5 w-5 text-emerald-600" />
                  {t('filters.title')}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t('filters.descriptionSrOnly')}
                </DialogDescription>
              </DialogHeader>
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto bg-white">
                <MobileFilterContent
                  isFilterUpdatePending={isFilterUpdatePending}
                  activityFilters={activityFilters}
                  onActivityFilterChange={handleActivityFilterChange}
                  selectedAnimalTypes={selectedAnimalTypes}
                  onAnimalTypeChange={handleAnimalTypeChange}
                  selectedAdventureTypes={selectedAdventureTypes}
                  onAdventureTypeChange={handleAdventureTypeChange}
                  hidePlaceboFilters={hidePlaceboFilters}
                  selectedSeason={selectedSeason}
                  onSeasonChange={handleSeasonChange}
                  groupSize={groupSize}
                  onGroupSizeChange={handleGroupSizeChange}
                  showClearFilters={showClearFilters}
                  onClearFilters={onClearFilters}
                  onClose={() => setMobileFilterOpen(false)}
                />
              </div>
            </DialogContent>
          </Dialog>
        </>
      );
    }
    return (
      <Popover open={compactOpen} onOpenChange={setCompactOpen}>
        <PopoverTrigger asChild>{compactTrigger}</PopoverTrigger>
        <PopoverContent className="w-[min(90vw,400px)] max-h-[85vh] overflow-y-auto p-0 bg-white border border-gray-200 shadow-lg" align="start">
          <div className="p-4 border-b border-gray-100 bg-white">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="h-4 w-4 text-primary" />
              {t('filters.title')}
            </h3>
          </div>
          <div className="p-4 max-h-[70vh] overflow-y-auto bg-white">
            <MobileFilterContent
              isFilterUpdatePending={isFilterUpdatePending}
              activityFilters={activityFilters}
              onActivityFilterChange={handleActivityFilterChange}
              selectedAnimalTypes={selectedAnimalTypes}
              onAnimalTypeChange={handleAnimalTypeChange}
              selectedAdventureTypes={selectedAdventureTypes}
              onAdventureTypeChange={handleAdventureTypeChange}
              hidePlaceboFilters={hidePlaceboFilters}
              selectedSeason={selectedSeason}
              onSeasonChange={handleSeasonChange}
              groupSize={groupSize}
              onGroupSizeChange={handleGroupSizeChange}
              showClearFilters={showClearFilters}
              onClearFilters={onClearFilters}
              onClose={() => setCompactOpen(false)}
            />
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Mobile Version - Single Filter Button with Modal
  if (isMobile) {
    return (
      <div className="flex items-center justify-center px-4 py-3">
        <motion.div 
          className="flex items-center gap-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {/* Filter Status Indicator */}
          <AnimatePresence>
            {isFilterUpdatePending && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-2 px-3 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium shadow-sm"
              >
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>{t('filters.updatingFilters')}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mobile Filter Button */}
          <Dialog open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className={cn(
                  "h-12 rounded-xl border-2 gap-3 bg-white/95 backdrop-blur-sm hover:bg-white transition-all duration-200 shadow-lg hover:shadow-xl px-4",
                  isFilterUpdatePending && "opacity-75",
                  activeFilterCount > 0 && "border-emerald-500 bg-emerald-50 text-emerald-700"
                )}
              >
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center",
                    activeFilterCount > 0 
                      ? "bg-emerald-500 text-white" 
                      : "bg-slate-100 text-slate-600"
                  )}>
                    <Sliders className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="font-semibold text-sm">
                      {activeFilterCount > 0 ? t('filters.filtersCount', { count: activeFilterCount }) : t('filters.title')}
                    </span>
                    {activeFilterCount > 0 && (
                      <span className="text-xs text-slate-500">
                        {t('filters.tapToModify')}
                      </span>
                    )}
                  </div>
                </div>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md w-[95vw] max-h-[90vh] overflow-hidden p-0">
              <DialogHeader className="px-6 py-4 border-b">
                <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Sliders className="h-5 w-5 text-emerald-600" />
                  {t('filters.filterExperiences')}
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  {t('filters.description')}
                </DialogDescription>
              </DialogHeader>
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                <MobileFilterContent
                  isFilterUpdatePending={isFilterUpdatePending}
                  activityFilters={activityFilters}
                  onActivityFilterChange={handleActivityFilterChange}
                  selectedAnimalTypes={selectedAnimalTypes}
                  onAnimalTypeChange={handleAnimalTypeChange}
                  selectedAdventureTypes={selectedAdventureTypes}
                  onAdventureTypeChange={handleAdventureTypeChange}
                  hidePlaceboFilters={hidePlaceboFilters}
                  selectedSeason={selectedSeason}
                  onSeasonChange={handleSeasonChange}
                  groupSize={groupSize}
                  onGroupSizeChange={handleGroupSizeChange}
                  showClearFilters={showClearFilters}
                  onClearFilters={onClearFilters}
                  onClose={() => setMobileFilterOpen(false)}
                />
              </div>
            </DialogContent>
          </Dialog>

          {/* Clear Filters - Mobile */}
          <AnimatePresence>
            {showClearFilters && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={onClearFilters}
                  className="h-12 text-emerald-600 bg-white/95 hover:bg-emerald-50 backdrop-blur-sm rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2 px-3"
                >
                  <X className="h-4 w-4" />
                  {t('filters.clear')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    );
  }

  // Desktop Version - Original Horizontal Layout  
  return (
    <div className="flex items-center justify-center px-4 py-2 border-t border-gray-200/10 overflow-x-auto bg-transparent scrollbar-hide">
      <motion.div 
        className="flex items-center gap-3 max-w-4xl mx-auto flex-wrap justify-center"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        {/* Filter Status Indicator */}
        <AnimatePresence>
          {isFilterUpdatePending && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-2 px-3 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium shadow-sm"
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>{t('filters.updatingFilters')}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Experience Type Filter */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "h-10 rounded-full border-gray-300/50 gap-2 bg-white/95 backdrop-blur-sm hover:bg-white/100 transition-all duration-200 shadow-sm hover:shadow-md",
                isFilterUpdatePending && "opacity-75"
              )}
            >
              <Filter className="h-4 w-4 text-primary" />
              <span className="text-gray-700">{t('filters.experienceType')}</span>
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-6 bg-white/95 backdrop-blur-sm shadow-xl border border-gray-200/50 rounded-xl">
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">{t('filters.experienceTypes')}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleActivityFilterChange({
                    showAnimalActivities: true,
                    showAdventureActivities: true
                  })}
                  className="text-sm text-primary hover:text-[#1F4220]"
                >
                  {t('filters.reset')}
                </Button>
              </div>
              
              <div className="space-y-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activityFilters.showAnimalActivities}
                    onChange={(e) => handleActivityFilterChange({
                      ...activityFilters,
                      showAnimalActivities: e.target.checked
                    })}
                    className="w-4 h-4 text-primary bg-white border-gray-300 rounded focus:ring-primary"
                  />
                  <Fish className="h-4 w-4 text-primary" />
                  <span className="text-gray-700">{t('filters.animalExperiences')}</span>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activityFilters.showAdventureActivities}
                    onChange={(e) => handleActivityFilterChange({
                      ...activityFilters,
                      showAdventureActivities: e.target.checked
                    })}
                    className="w-4 h-4 text-primary bg-white border-gray-300 rounded focus:ring-primary"
                  />
                  <Mountain className="h-4 w-4 text-primary" />
                  <span className="text-gray-700">{t('filters.adventureExperiences')}</span>
                </label>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Animal Type Filter - Conditional */}
        <AnimatePresence>
          {showAnimalFilter && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, x: -10 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.95, x: -10 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={cn(
                      "h-10 rounded-full border-gray-300/50 gap-2 bg-white/95 backdrop-blur-sm hover:bg-white/100 transition-all duration-200 shadow-sm hover:shadow-md",
                      isFilterUpdatePending && "opacity-75"
                    )}
                  >
                    <Fish className="h-4 w-4 text-primary" />
                    <span className="text-gray-700">{t('filters.animals')}</span>
                    {selectedAnimalTypes.length > 0 && (
                      <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                        {selectedAnimalTypes.length}
                      </span>
                    )}
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-6 bg-white/95 backdrop-blur-sm shadow-xl border border-gray-200/50 rounded-xl">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900">{t('filters.animalTypes')}</h3>
                      {selectedAnimalTypes.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAnimalTypeChange([])}
                          className="text-sm text-primary hover:text-[#1F4220]"
                        >
                          {t('filters.clearAll')}
                        </Button>
                      )}
                    </div>
                    <AnimalTypeFilter
                      selected={selectedAnimalTypes}
                      onChange={handleAnimalTypeChange}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Adventure Type Filter - Conditional */}
        <AnimatePresence>
          {showAdventureFilter && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, x: -10 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.95, x: -10 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={cn(
                      "h-10 rounded-full border-gray-300/50 gap-2 bg-white/95 backdrop-blur-sm hover:bg-white/100 transition-all duration-200 shadow-sm hover:shadow-md",
                      isFilterUpdatePending && "opacity-75"
                    )}
                  >
                    <Mountain className="h-4 w-4 text-primary" />
                    <span className="text-gray-700">{t('filters.adventures')}</span>
                    {selectedAdventureTypes.length > 0 && (
                      <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                        {selectedAdventureTypes.length}
                      </span>
                    )}
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-6 bg-white/95 backdrop-blur-sm shadow-xl border border-gray-200/50 rounded-xl">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900">{t('filters.adventureTypes')}</h3>
                      {selectedAdventureTypes.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAdventureTypeChange([])}
                          className="text-sm text-primary hover:text-[#1F4220]"
                        >
                          {t('filters.clearAll')}
                        </Button>
                      )}
                    </div>
                    <AdventureTypeFilter
                      selected={selectedAdventureTypes}
                      onChange={handleAdventureTypeChange}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </motion.div>
          )}
        </AnimatePresence>

        {!hidePlaceboFilters && (
          <>
            {/* When Available Dropdown - not applied to results yet */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="h-10 rounded-full border-gray-300/50 gap-2 bg-white/95 backdrop-blur-sm hover:bg-white/100 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="text-gray-700">
                    {selectedSeason ? SEASONS.find(s => s.id === selectedSeason)?.name || t('filters.season') : t('filters.season')}
                  </span>
                  {selectedSeason && (
                    <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                      {SEASONS.find(s => s.id === selectedSeason)?.emoji}
                    </span>
                  )}
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-6 bg-white/95 backdrop-blur-sm shadow-xl border border-gray-200/50 rounded-xl">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">{t('filters.bestSeasonToVisit')}</h3>
                    {selectedSeason && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSeasonChange(null)}
                        className="text-sm text-primary hover:text-[#1F4220]"
                      >
                        {t('filters.clear')}
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {SEASONS.map((season) => {
                      const isSelected = selectedSeason === season.id;
                      const isCurrent = getCurrentSeason().id === season.id;
                      
                      return (
                        <button
                          key={season.id}
                          onClick={() => handleSeasonChange(isSelected ? null : season.id)}
                          className={cn(
                            "p-3 rounded-lg border-2 transition-all duration-200 text-left",
                            isSelected
                              ? "bg-primary/10 border-primary text-primary"
                              : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-primary"
                          )}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm">{season.emoji}</span>
                            <span className="font-medium text-sm">{season.name}</span>
                            {isCurrent && !isSelected && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                                {t('filters.current')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600">{season.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Group Size Dropdown - not applied to results yet */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="h-10 rounded-full border-gray-300/50 gap-2 bg-white/95 backdrop-blur-sm hover:bg-white/100 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Users className="h-4 w-4 text-primary" />
                  <span className="text-gray-700">
                    {groupSize[0] === 1 && groupSize[1] === 20
                      ? t('filters.groupSize')
                      : groupSize[0] === groupSize[1]
                        ? t('filters.personCount', { count: groupSize[0] })
                        : t('filters.peopleRange', { min: groupSize[0], max: groupSize[1] })
                    }
                  </span>
                  {(groupSize[0] !== 1 || groupSize[1] !== 20) && (
                    <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                      {groupSize[0] === groupSize[1] ? groupSize[0] : `${groupSize[0]}-${groupSize[1]}`}
                    </span>
                  )}
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-6 bg-white/95 backdrop-blur-sm shadow-xl border border-gray-200/50 rounded-xl">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">{t('filters.selectGroupSize')}</h3>
                    {(groupSize[0] !== 1 || groupSize[1] !== 20) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleGroupSizeChange([1, 20])}
                        className="text-sm text-primary hover:text-[#1F4220]"
                      >
                        {t('filters.reset')}
                      </Button>
                    )}
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">{t('filters.quickSelect')}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                        <Button 
                          key={num} 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleGroupSizeChange([num, num])}
                          className={cn(
                            "w-full transition-colors border-2",
                            groupSize[0] === num && groupSize[1] === num
                              ? "bg-primary/10 border-primary text-primary"
                              : "bg-white hover:bg-gray-50 border-gray-200 hover:border-primary"
                          )}
                        >
                          {num}
                        </Button>
                      ))}
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleGroupSizeChange([9, 20])}
                      className={cn(
                        "w-full mt-2 transition-colors border-2",
                        groupSize[0] >= 9 && groupSize[1] >= 9
                          ? "bg-primary/10 border-primary text-primary"
                          : "bg-white hover:bg-gray-50 border-gray-200 hover:border-primary"
                      )}
                    >
                      {t('filters.ninePlusPeople')}
                    </Button>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-700">{t('filters.orSelectRange')}</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">{t('filters.minimum')}</label>
                        <select 
                          value={groupSize[0]}
                          onChange={(e) => {
                            const min = parseInt(e.target.value);
                            handleGroupSizeChange([min, Math.max(min, groupSize[1])]);
                          }}
                          className="w-full p-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          {Array.from({length: 20}, (_, i) => i + 1).map(num => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">{t('filters.maximum')}</label>
                        <select 
                          value={groupSize[1]}
                          onChange={(e) => {
                            const max = parseInt(e.target.value);
                            handleGroupSizeChange([Math.min(groupSize[0], max), max]);
                          }}
                          className="w-full p-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          {Array.from({length: 20}, (_, i) => i + 1).filter(num => num >= groupSize[0]).map(num => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}

        {/* Clear Filters Button */}
        <AnimatePresence>
          {showClearFilters && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <Button 
                variant="ghost" 
                size="sm"
                onClick={onClearFilters}
                className="h-10 text-primary bg-white/95 hover:bg-white/100 backdrop-blur-sm rounded-full transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                {t('filters.clearFilters')}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
} 